import { AppRoutes } from "AppRoutes";
import consentConfig from "consentConfig";
import AppError from "components/AppError";
import FullscreenLoader from "components/FullscreenLoader";
import NotFound from "components/NotFound";
import { authenticateUserWithLiveChat, initLiveChat } from "lib/liveChat";
import { isChunkError, lazyWithRetry } from "@/lib/lazyWithRetry";
import { identifyUser, reportError } from "lib/sentry";
import tracker from "lib/tracker";
import { set } from "lodash";
// @ts-expect-error side-effect import
import "moment/locale/de";
import { usePostHog } from "posthog-js/react";
import React, { Suspense } from "react";
import * as Sentry from "@sentry/react";
import { HelmetProvider } from "react-helmet-async";
import { useAuth } from "react-oidc-context";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { APIContext } from "utils/api";
import { API2Context } from "utils/api2";
import { getAuthorizationVersionHeader } from "utils/oidc";

const LoadableSharedReport = lazyWithRetry(
	() => import("modules/reports/page/SharedReportPage"),
);

import * as CookieConsent from "vanilla-cookieconsent";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import { ThemeProvider } from "./components/ThemeProvider";
import { Toaster } from "./components/ui/sonner";

if (!(window as any).Cypress) {
	initLiveChat();
}

type AuthState = {
	initial_url?: string;
};

const NotAuthenticated: React.FC = () => {
	const auth = useAuth();
	React.useEffect(() => {
		auth.signinRedirect({
			state: {
				initial_url: window.location.pathname + window.location.search,
			},
		});
	}, [auth]);
	return null;
};

const Callback = () => {
	const auth = useAuth();
	if (auth.isAuthenticated) {
		const state = auth.user?.state as AuthState | undefined;
		return <Navigate to={state?.initial_url ?? "/"} replace />;
	}
	if (auth.error) {
		reportError(auth.error);
	}
	const manualPath = `${window.location.protocol}//${window.location.host}`;
	return (
		<div>
			Automatische Weiterleitung...
			<br />
			<a href={manualPath}>
				Hier klicken, falls die weiterleitung nicht funktioniert.
			</a>
		</div>
	);
};

const RequireAuth: React.FC<{ children?: React.ReactNode }> = ({
	children,
}) => {
	const auth = useAuth();
	React.useEffect(() => {
		return auth.events.addAccessTokenExpired(() => {
			if (!auth.isAuthenticated && !auth.isLoading && !auth.activeNavigator) {
				console.warn("token expired");
				auth.signinRedirect({
					state: {
						initial_url: window.location.pathname + window.location.search,
					},
				});
			}
		});
	});

	if (auth.isLoading) {
		return <FullscreenLoader />;
	}
	if (auth.error) {
		reportError(auth.error);
		throw auth.error;
	}

	if (!auth.isAuthenticated) {
		return <NotAuthenticated />;
	}
	if (auth.isAuthenticated) {
		return children as React.ReactElement;
	}
	return <FullscreenLoader />;
};

const App: React.FC = () => {
	const auth = useAuth();
	const api = React.useContext(APIContext);
	const api2 = React.useContext(API2Context);

	if (auth.user && auth.isAuthenticated) {
		const user = auth.user;
		set(
			api.defaults,
			"headers.common.Authorization",
			`Bearer ${user?.access_token}`,
		);
		set(
			api2.defaults,
			"headers.common.Authorization",
			`Bearer ${user?.access_token}`,
		);
		set(
			api.defaults,
			"headers.common.auth-version",
			getAuthorizationVersionHeader(),
		);
		tracker.identify(user);
		identifyUser(user);
		setTimeout(async () => {
			try {
				if (user.profile.email) {
					authenticateUserWithLiveChat(user);
				}
			} catch (err) {
				console.error("could not authenticate with help chat");
				reportError(err);
			}
		}, 0);
		console.debug("configured api to use access token and identified user");
	}

	React.useEffect(() => {
		return auth.events.addSilentRenewError((err: Error) => {
			reportError(err);
			auth.clearStaleState();
		});
	});
	const posthog = usePostHog();
	React.useEffect(() => {
		const setupPosthog = (cookie: CookieConsent.CookieValue) => {
			if (cookie.categories.includes("analytics")) {
				posthog.opt_in_capturing();
			} else {
				posthog.opt_out_capturing();
			}
		};
		CookieConsent.run({
			onConsent({ cookie }) {
				setupPosthog(cookie);
			},
			onChange({ cookie }) {
				setupPosthog(cookie);
			},
			...consentConfig,
		});
	}, [posthog]);
	return (
		<HelmetProvider>
			<ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
				<BrowserRouter>
					<Toaster position="bottom-left" />
					<Routes>
						{/* PUBLIC ROUTES */}
						<Route
							path="/shared-report"
							element={
								<Suspense fallback={<div>Loading ...</div>}>
									<LoadableSharedReport />
								</Suspense>
							}
						/>
						{/* AUTH ROUTES */}
						<Route path="/login" element={<Navigate to="/" />} />
						<Route path="/logout" element={<Navigate to="/" />} />
						<Route path="/callback" element={<Callback />} />
						{/* SECURE DASHBOARD ROUTES */}
						<Route
							path="/*"
							element={
								<RequireAuth>
									<AppRoutes />
								</RequireAuth>
							}
						/>
						{/* Not found */}
						<Route path="*" element={<NotFound />} />
					</Routes>
				</BrowserRouter>
			</ThemeProvider>
		</HelmetProvider>
	);
};

export default Sentry.withErrorBoundary(App, {
	fallback: ({ error, resetError }) => (
		<AppError error={error} resetErrorBoundary={resetError} />
	),
	onError: (error, componentStack, eventId) => {
		// Log to console so the error is visible during mobile / remote debugging,
		// where opening Sentry isn't practical. Sentry still receives the event via
		// withErrorBoundary's default capture.
		if (isChunkError(error)) {
			console.warn("New version available.");
			return;
		}
		console.error(
			`[AppErrorBoundary] Sentry event ${eventId}:`,
			error,
			"\nComponent stack:",
			componentStack,
		);
	},
});
